import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Loader } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppForm } from "@/hooks/useCreateForm";

export const Route = createFileRoute("/_authed/_org/settings/change-email/")({
  component: RouteComponent,
});

const formValidator = z.object({
  code: z.string().length(6, "Verification code must be 6 digits"),
  newEmail: z.string().email("Please enter a valid email address"),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { trpc } = Route.useRouteContext();
  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());

  const { mutate: resendOTP, isPending: resendCodeSubmitting } = useMutation(
    trpc.auth.sendOTPChallenge.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Verification code sent");
      },
    }),
  );

  const { mutate: requestChangeEmail, isPending: changeEmailSubmitting } =
    useMutation(
      trpc.auth.requestChangeEmail.mutationOptions({
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: () => {
          toast.success("Verification email sent to your new address");
          navigate({ to: "/settings/change-email/confirm" });
        },
      }),
    );

  const form = useAppForm({
    defaultValues: {
      code: "",
      newEmail: "",
    },
    onSubmit: ({ value }) => {
      requestChangeEmail({
        challengeCode: value.code,
        newEmail: value.newEmail,
      });
    },
    validators: {
      onSubmit: formValidator,
    },
  });

  return (
    <div className="p-4">
      <Button
        className="mb-4 inline-flex items-center"
        onClick={() => navigate({ to: "/settings" })}
        variant="ghost"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Settings
      </Button>

      <Card className="mx-auto max-w-md">
        <CardHeader>
          <CardTitle>Change Email Address</CardTitle>
          <CardDescription>
            Change from {user?.email}. You will have to verify your new email
            address
          </CardDescription>
        </CardHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <CardContent className="space-y-4">
            <form.AppField
              children={(field) => {
                return (
                  <field.OTPField
                    description="Enter the 6-digit code sent to your phone"
                    label="Verification Code"
                    required={true}
                  />
                );
              }}
              name="code"
            />

            <form.AppField
              children={(field) => {
                return (
                  <field.TextField
                    label="New Email Address"
                    placeholder="Enter your new email address"
                    required={true}
                  />
                );
              }}
              name="newEmail"
            />
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              className="w-full"
              disabled={changeEmailSubmitting}
              type="submit"
            >
              {changeEmailSubmitting ? "Submitting..." : "Submit"}
              {changeEmailSubmitting ? (
                <Loader className="ml-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4" />
              )}
            </Button>

            <Button
              className="font-medium text-primary text-sm"
              disabled={resendCodeSubmitting}
              onClick={() => resendOTP({ type: "email" })}
              type="button"
              variant="ghost"
            >
              Resend verification code
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
